/**
 * PaymentStep.js — Generic payment wizard step for Business Single Application V2.
 *
 * Handles the cart/checkout flow for V2 onboarding (types 1 and 2 only).
 * Type 3 (Upgrade) does not require payment (existing cyclosUserId).
 *
 * Uses the shared PayNow component but wraps it with V2-specific navigation:
 * - Back navigates to the previous wizard step (Documents).
 * - Submit navigates to the next wizard step (ThankYou) after final submission.
 *
 * This is NOT the same as BplusCheckout which is a standalone page.
 * PaymentStep is embedded within the V2 wizard sequence.
 */

import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import PayNow from '../../../../components/Payment/PayNow';
import { DialogContext } from '../../../../store/context/DialogContext';
import { doPUT } from '../../../../util/HttpUtil';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import { hydrateBusinessV2PayloadFromApi, normalizeBusinessV2PayloadForApi } from './compatibilityV2';
import { V2_TYPE, WIZARD_NUM } from './helperV2';
import { finalizeBusinessV2Submission } from './submitBusinessV2';

const mergePaymentUpdateData = ({
    previousData = {},
    fallbackData = {},
    responseData = {},
    applicationForm,
}) => {
    const hydratedResponse = hydrateBusinessV2PayloadFromApi(responseData ?? {});
    return {
        ...(previousData || {}),
        ...(fallbackData || {}),
        ...(hydratedResponse || {}),
        formType:
            applicationForm?.formType ??
            hydratedResponse?.formType ??
            fallbackData?.formType ??
            previousData?.formType,
        type:
            applicationForm?.type ??
            hydratedResponse?.type ??
            fallbackData?.type ??
            previousData?.type,
        interestedInPrime:
            hydratedResponse?.interestedInPrime ??
            fallbackData?.interestedInPrime ??
            previousData?.interestedInPrime,
    };
};

const PaymentStep = ({
    data,
    editId,
    applicationForm,
    cyclosFields,
    handleUpdate,
    handlePrev,
    setData,
    setHasError,
    asLabel,
    wizard,
    pageNum = WIZARD_NUM.PayNow,
    v2Type = V2_TYPE.PLUS,
    jsonConfig,
}) => {
    const { showError } = useContext(DialogContext);
    const navigate = useNavigate();
    // Broker applications (brokerId present) are never charged.
    const paymentWaived = Boolean(applicationForm?.brokerId) || Boolean(data?.brokerId);
    // Business Prime upgrades (type 3) are existing Business Plus customers and
    // were never charged in the legacy upgrade flow. The top-level
    // applicationForm.cyclosUserId is not always populated for these (the id can
    // live on the connected Plus application / individual), so gate the waiver on
    // the application type rather than that field alone.
    const isUpgradeApplication = Number(applicationForm?.type ?? v2Type) === V2_TYPE.UPGRADE;

    // Prepare data for PayNow (it expects formType on the data object)
    const payNowData = useMemo(() => ({
        ...data,
        formType: "BUSINESS",
    }), [data]);

    const handlePaymentBackClick = () => {
        if (typeof handlePrev === 'function') {
            handlePrev();
        }
    };

    const handlePaymentSubmit = () => finalizeBusinessV2Submission({
        editId,
        data,
        applicationForm,
        type: v2Type,
        wizard,
        jsonConfig,
        handleUpdate,
        setHasError,
        showError,
        navigate,
    });

    const handleUpdateFormData = async (payload) => {
        try {
            const nextPayload = payload ?? data;
            const response = await doPUT(`/api/business/update`, normalizeBusinessV2PayloadForApi(nextPayload, cyclosFields));
            if (response?.status === 200) {
                const nextData = mergePaymentUpdateData({
                    previousData: data,
                    fallbackData: nextPayload,
                    responseData: response?.data,
                    applicationForm,
                });
                setData?.(nextData);
                return {
                    ...response,
                    data: nextData,
                };
            }
            return response;
        } catch (error) {
            showError(error);
            return { status: 400 };
        }
    };

    // Don't render payment for applications that don't require client payment.
    if (paymentWaived || isUpgradeApplication || applicationForm?.cyclosUserId) {
        return (
            <div className='py-3' data-testid='business-v2-payment-step'>
                <h2 className='pt-3'>Payment</h2>
                <p className='text-muted small'>
                    {paymentWaived
                        ? "Payment is waived for this sponsored application. Continue to submit the application."
                        : "Payment is not required for this application. Continue to submit the application."}
                </p>
                <ButtonGroupNew
                    onNextClick={handlePaymentSubmit}
                    onBackClick={handlePaymentBackClick}
                    showPageNum={false}
                    pageNum={pageNum}
                    step={pageNum}
                    isDisabled={asLabel}
                    asLabel={asLabel}
                    loading={false}
                />
            </div>
        );
    }

    if (asLabel) {
        return (
            <div className='py-3' data-testid='business-v2-payment-step'>
                <div className='fw-semibold'>Payment</div>
                <div className='text-muted small'>Payment details are only visible during the application flow.</div>
            </div>
        );
    }

    return (
        <div data-testid='business-v2-payment-step'>
            <PayNow
                data={payNowData}
                handleUpdateFormData={handleUpdateFormData}
                setHasError={setHasError}
                onBackClick={handlePaymentBackClick}
                onSubmit={handlePaymentSubmit}
            />
        </div>
    );
};

export default observer(PaymentStep);
